import { Button } from '@bambi/component-library/src/components/Button';
import { ContextFormFieldSelect } from '@bambi/component-library/src/components/forms/context/ContextFormFieldSelect';
import { ContextFormFieldTextArea } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTextArea';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import type {
  PreferenceDirection,
  PreferenceStrictness,
} from './preferenceType';
import {
  directionOptions,
  fromPreferenceType,
  strictnessOptions,
  toPreferenceType,
} from './preferenceType';
import { useAppDispatch } from 'src/app/store/hooks';
import type {
  DriverRead,
  PreferredDriverRead,
} from 'src/common/external/bambi-api/bambiApi';
import {
  usePassengersPreferredDriversCreateMutation,
  usePassengersPreferredDriversPartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { Modal } from 'src/common/primitives/Modal/Modal';
import { RadioGroup } from 'src/common/primitives/RadioGroup/RadioGroup';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { formatFullName } from 'src/common/util/formatFullName';
import formatServerError from 'src/common/util/serverErrorFormatter';

export type PreferredDriverFormValues = {
  driver?: string;
  direction?: PreferenceDirection;
  strictness?: PreferenceStrictness;
  notes?: string;
};

interface PreferredDriverFormModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose?: () => void;
  passengerId: string;
  passengerName: string;
  preferredDriver?: PreferredDriverRead;
  drivers: DriverRead[];
}

function defaultsFor(
  preferredDriver?: PreferredDriverRead
): PreferredDriverFormValues {
  if (!preferredDriver) {
    return {
      driver: undefined,
      direction: undefined,
      strictness: undefined,
      notes: undefined,
    };
  }
  const { direction, strictness } = fromPreferenceType(
    preferredDriver.preference_type
  );
  return {
    driver: preferredDriver.driver,
    direction,
    strictness,
    notes: preferredDriver.notes,
  };
}

export function PreferredDriverFormModal({
  open,
  setOpen,
  onClose = () => {},
  passengerId,
  passengerName,
  preferredDriver,
  drivers,
}: PreferredDriverFormModalProps) {
  const dispatch = useAppDispatch();
  const isEditing = !!preferredDriver;

  const driverOptions = drivers.map((driver) => ({
    label: formatFullName(driver),
    value: driver.id,
  }));

  const methods = useForm<PreferredDriverFormValues>({
    defaultValues: defaultsFor(preferredDriver),
  });
  const { handleSubmit, reset, control } = methods;

  useEffect(() => {
    if (open) {
      reset(defaultsFor(preferredDriver));
    }
  }, [open, preferredDriver, reset]);

  const [createPreference] = usePassengersPreferredDriversCreateMutation();
  const [updatePreference] =
    usePassengersPreferredDriversPartialUpdateMutation();

  const handleClose = () => {
    reset();
    setOpen(false);
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!data.driver || !data.direction || !data.strictness) {
        // Should be caught by form validation; guard for type narrowing.
        return;
      }

      const preferenceType = toPreferenceType(data.direction, data.strictness);

      if (isEditing) {
        await updatePreference({
          passengerId,
          id: preferredDriver.id,
          patchedPreferredDriver: {
            driver: data.driver,
            preference_type: preferenceType,
            notes: data.notes,
          },
        }).unwrap();
        dispatch(show({ title: 'Driver rule updated', type: 'success' }));
      } else {
        await createPreference({
          passengerId,
          preferredDriver: {
            driver: data.driver,
            preference_type: preferenceType,
            notes: data.notes,
          },
        }).unwrap();
        dispatch(show({ title: 'Driver rule added', type: 'success' }));
      }
      handleClose();
    } catch (e) {
      dispatch(
        show({
          title: isEditing
            ? 'Failed to update driver rule'
            : 'Failed to add driver rule',
          description: formatServerError(e),
          type: 'error',
        })
      );
    }
  });

  const title = `${
    isEditing ? 'Edit' : 'Add'
  } Driver Rule for ${passengerName}`;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      contentClassnames="w-[480px]"
      title={title}
    >
      <div className="flex flex-col gap-4">
        <h4 className="text-lg font-medium text-gray-900">{title}</h4>
        <FormProvider {...methods}>
          <ContextFormFieldSelect
            fieldPath="driver"
            label="Driver"
            options={driverOptions}
            placeholder="Select a driver"
            unwrapValue
            rules={{ required: 'Driver is required' }}
          />
          <Controller
            control={control}
            name="direction"
            rules={{ required: 'Pairing direction is required' }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1">
                <RadioGroup<PreferenceDirection>
                  label="Pairing direction *"
                  options={directionOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
                {fieldState.error?.message && (
                  <p
                    className="text-sm text-red-600"
                    data-testid="direction-error"
                  >
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
          <Controller
            control={control}
            name="strictness"
            rules={{ required: 'Strictness is required' }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1">
                <RadioGroup<PreferenceStrictness>
                  label="Strictness *"
                  options={strictnessOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
                {fieldState.error?.message && (
                  <p
                    className="text-sm text-red-600"
                    data-testid="strictness-error"
                  >
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
          <ContextFormFieldTextArea
            fieldPath="notes"
            label="Notes (optional)"
          />
        </FormProvider>
      </div>
      <div className="mt-4 flex flex-row justify-between gap-2">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          Save
        </Button>
      </div>
    </Modal>
  );
}
